import {
  BREAKPOINTS, ELEMENT_STATES,
  type Breakpoint, type ElementState, type ElementStylesDto, type StyleBlockDto
} from '@xshop/shared';

// The cascade: `base` is the design, `tablet` and `mobile` override it downwards, and `default` is the
// state the other three are variations on. An entry an element does not carry inherits from the width
// above it — it must never reset a value the merchant set once at the design width.
//
// Only what a merchant actually set is written — never a reset, and never a copy of the width above. The
// inheritance is then the browser's own cascade, which is only safe because the rules are written so that
// nothing but the merchant's intent can decide between them: every rule wears the element's class twice,
// so a resting rule and a state rule can no longer be separated by an accident of specificity, and the
// rules are written state by state, so every width's resting look is settled before the first `:hover`.
//
// `effectiveBlock` is the other half of the same idea: what the browser will end up applying at one width
// in one state, which is what the inspector shows a merchant who has selected an element on a phone.

/** The width each breakpoint takes over at; the builder frames a tablet at 820px and a phone at 390px,
 * and both land inside the band below them. */
export const BREAKPOINT_WIDTHS: Record<Breakpoint, number | null> = { base: null, tablet: 1024, mobile: 640 };

/** One width and state of an element, and the block of properties actually in force there. */
export type StyleLayer = { breakpoint: Breakpoint; state: ElementState; block: StyleBlockDto };

// Answers the media query a breakpoint's rules sit behind, or null for the base design, which needs none.
export function breakpointMedia(breakpoint: Breakpoint): string | null {
  const width = BREAKPOINT_WIDTHS[breakpoint];
  return width === null ? null : `(max-width: ${width}px)`;
}

// Answers the band of widths a breakpoint owns alone, which is the band `hiddenOn` switches an element off in.
export function breakpointBand(breakpoint: Breakpoint): string {
  if (breakpoint === 'mobile') return `(max-width: ${BREAKPOINT_WIDTHS.mobile}px)`;
  const floor = (BREAKPOINT_WIDTHS.mobile ?? 0) + 1;
  if (breakpoint === 'tablet') return `(min-width: ${floor}px) and (max-width: ${BREAKPOINT_WIDTHS.tablet}px)`;
  return `(min-width: ${(BREAKPOINT_WIDTHS.tablet ?? 0) + 1}px)`;
}

// Lists the widths one breakpoint inherits from, widest first, so an absent entry inherits and never resets.
export function breakpointChain(breakpoint: Breakpoint): readonly Breakpoint[] {
  return BREAKPOINTS.slice(0, BREAKPOINTS.indexOf(breakpoint) + 1);
}

// Merges the chain into the block in force at one width in one state: every width's resting look first,
// then every width's own take on the state, so a state refines the default rather than replacing it.
export function effectiveBlock(styles: ElementStylesDto | undefined, breakpoint: Breakpoint, state: ElementState): StyleBlockDto {
  const chain = breakpointChain(breakpoint);
  let block: StyleBlockDto = {};
  for (const width of chain) block = { ...block, ...styles?.[width]?.default };
  if (state === 'default') return block;
  for (const width of chain) block = { ...block, ...styles?.[width]?.[state] };
  return block;
}

// Lists the rules worth writing: every block a merchant actually filled in, state by state and then width
// by width, which is the order the rules must appear in for the browser to settle them the same way.
export function styleLayers(styles: ElementStylesDto | undefined): StyleLayer[] {
  const layers: StyleLayer[] = [];
  for (const state of ELEMENT_STATES) {
    for (const breakpoint of BREAKPOINTS) {
      const block = styles?.[breakpoint]?.[state];
      if (block !== undefined && Object.keys(block).length > 0) layers.push({ breakpoint, state, block });
    }
  }
  return layers;
}
